import React from "react";
import { useReducedMotion } from "framer-motion";
import { Lock, Database, Cpu, Wrench, FlaskConical, FileText, Building2, User } from "lucide-react";

/*
 * ArchitectureDiagram — Streamline360 architecture (horizontal, left-to-right).
 *
 * Flow:
 *   6 plant data sources (INSIDE boundary)
 *     → Data-Blind Boundary wall
 *     → METADATA splits into two streams:
 *         (top)    → SLM on-premise / LLM block
 *         (bottom) → Knowledge Graph
 *     → USER INPUT also feeds Knowledge Graph
 *     → Knowledge Graph feeds SLM/LLM
 *     → SLM/LLM → SPECIFIC AGENT → RESPONSE · TRUSTED OUTPUT (hero)
 */

const SOURCES = [
  { label: "Process Historian", icon: Database },
  { label: "DCS / PLC / SCADA", icon: Cpu },
  { label: "Maintenance (CMMS)", icon: Wrench },
  { label: "Laboratory", icon: FlaskConical },
  { label: "Documents & SOPs", icon: FileText },
  { label: "Enterprise Systems", icon: Building2 },
];

// Canvas
const W = 1620;
const H = 640;

// Sources (INSIDE boundary)
const SRC_X = 40;
const SRC_W = 200;
const SRC_H = 52;
const SRC_START_Y = 60;
const SRC_GAP = 14;
const sourceY = (i) => SRC_START_Y + i * (SRC_H + SRC_GAP);

// Vertical boundary
const BOUNDARY_X = 340;

// SLM / LLM block (OUTSIDE boundary)
const SLM_W = 300;
const SLM_H = 170;
const SLM_X = 720;
const SLM_Y = 190;

// Knowledge Graph oval (OUTSIDE boundary, below SLM/LLM)
const KG_CX = 640;
const KG_CY = 475;
const KG_RX = 95;
const KG_RY = 62;

// User Input (bottom, below KG)
const USER_W = 190;
const USER_H = 58;
const USER_X = KG_CX - USER_W / 2;
const USER_Y = 560;

// Specific Agent
const AGENT_W = 200;
const AGENT_H = 90;
const AGENT_X = 1100;
const AGENT_Y = SLM_Y + SLM_H / 2 - AGENT_H / 2; // vertically aligned with SLM center

// Response / Trusted Output (hero)
const OUT_W = 260;
const OUT_H = 170;
const OUT_X = 1350;
const OUT_Y = SLM_Y + SLM_H / 2 - OUT_H / 2;

export default function ArchitectureDiagram() {
  const reduce = useReducedMotion();

  // sources → boundary
  const sourceToBoundary = SOURCES.map((_, i) => {
    const x0 = SRC_X + SRC_W;
    const y0 = sourceY(i) + SRC_H / 2;
    return `M ${x0} ${y0} L ${BOUNDARY_X - 16} ${y0}`;
  });

  // Metadata TOP: boundary → SLM/LLM top-left
  const metaToSlm = `M ${BOUNDARY_X + 16} 230 L ${SLM_X - 4} ${SLM_Y + 42}`;
  // Metadata BOTTOM: boundary → KG left
  const metaToKg = `M ${BOUNDARY_X + 16} 475 L ${KG_CX - KG_RX - 4} ${KG_CY}`;

  // user → KG (upward)
  const userToKg = `M ${KG_CX} ${USER_Y - 2} L ${KG_CX} ${KG_CY + KG_RY + 4}`;

  // KG → SLM/LLM (upward diagonal)
  const kgToSlm = `M ${KG_CX + KG_RX * 0.55} ${KG_CY - KG_RY * 0.7} L ${SLM_X + SLM_W * 0.6} ${SLM_Y + SLM_H - 4}`;

  // SLM/LLM → Agent
  const slmToAgent = `M ${SLM_X + SLM_W + 4} ${SLM_Y + SLM_H / 2} L ${AGENT_X - 4} ${AGENT_Y + AGENT_H / 2}`;
  // Agent → Trusted Output
  const agentToOut = `M ${AGENT_X + AGENT_W + 4} ${AGENT_Y + AGENT_H / 2} L ${OUT_X - 4} ${OUT_Y + OUT_H / 2}`;

  // reusable animated particle component
  const Particle = ({ path, dur = 2.6, delay = 0, color = "#10B981", r = 3.6 }) =>
    reduce ? null : (
      <circle r={r} fill={color} filter="url(#particleGlow)">
        <animateMotion dur={`${dur}s`} repeatCount="indefinite" begin={`${delay}s`} path={path} />
      </circle>
    );

  // arrowhead triangle at a given point, pointing right/left/up based on dir
  const ArrowHead = ({ x, y, dir = "right", color = "#10B981", size = 9 }) => {
    let points;
    if (dir === "right") points = `${x - size},${y - size * 0.75} ${x - size},${y + size * 0.75} ${x},${y}`;
    else if (dir === "left") points = `${x + size},${y - size * 0.75} ${x + size},${y + size * 0.75} ${x},${y}`;
    else if (dir === "up") points = `${x - size * 0.75},${y + size} ${x + size * 0.75},${y + size} ${x},${y}`;
    else points = `${x - size * 0.75},${y - size} ${x + size * 0.75},${y - size} ${x},${y}`;
    return <polygon points={points} fill={color} />;
  };

  return (
    <div
      data-testid="architecture-diagram"
      className="relative mx-auto border border-emerald-500/20 overflow-hidden rounded-lg"
      style={{
        aspectRatio: `${W}/${H}`,
        width: "100%",
        maxWidth: W,
        background: "radial-gradient(ellipse at 60% 40%, #0F172A 0%, #060B16 70%, #04070E 100%)",
        boxShadow: "0 0 0 1px rgba(16,185,129,0.08), 0 20px 60px -20px rgba(0,0,0,0.8)",
      }}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Streamline360 architecture diagram"
      >
        <defs>
          {/* gradients */}
          <linearGradient id="flowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#38BDF8" />
            <stop offset="100%" stopColor="#10B981" />
          </linearGradient>
          <linearGradient id="skyGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#38BDF8" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="emeraldGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0.6" />
          </linearGradient>

          {/* block fills */}
          <linearGradient id="sourceFill" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#111C2E" />
            <stop offset="100%" stopColor="#0A1322" />
          </linearGradient>
          <linearGradient id="slmFill" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0E1A2E" />
            <stop offset="100%" stopColor="#070D1A" />
          </linearGradient>
          <linearGradient id="agentFill" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#102033" />
            <stop offset="100%" stopColor="#081222" />
          </linearGradient>
          <linearGradient id="outFill" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0A1F1A" />
            <stop offset="50%" stopColor="#0B2820" />
            <stop offset="100%" stopColor="#061611" />
          </linearGradient>
          <linearGradient id="userFill" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0F1C2E" />
            <stop offset="100%" stopColor="#081322" />
          </linearGradient>

          {/* KG radial glow */}
          <radialGradient id="kgGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.35" />
            <stop offset="60%" stopColor="#10B981" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="kgFill" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#0D2C22" />
            <stop offset="100%" stopColor="#07140F" />
          </radialGradient>

          {/* trusted output hero glow */}
          <radialGradient id="outGlow" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </radialGradient>

          {/* filters */}
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="particleGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="heroGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* dot pattern background */}
          <pattern id="dots" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.8" fill="#1E293B" opacity="0.55" />
          </pattern>
        </defs>

        <rect width={W} height={H} fill="url(#dots)" />

        {/* ===== DATA-BLIND BOUNDARY vertical wall ===== */}
        <rect
          x={BOUNDARY_X - 16}
          y="34"
          width="32"
          height={H - 68}
          fill="#0A1424"
          stroke="#10B981"
          strokeDasharray="4 5"
          strokeWidth="1.4"
          rx="2"
        />
        <rect x={BOUNDARY_X - 16} y="34" width="32" height={H - 68} fill="url(#emeraldGrad)" opacity="0.08" />
        <g transform={`translate(${BOUNDARY_X - 12}, 10)`}>
          <foreignObject x="0" y="0" width="24" height="26">
            <Lock size={22} color="#10B981" />
          </foreignObject>
        </g>
        <text
          x={BOUNDARY_X}
          y={H / 2 + 40}
          fill="#10B981"
          fontFamily="JetBrains Mono, monospace"
          fontSize="11"
          fontWeight="800"
          letterSpacing="3.2"
          textAnchor="middle"
          transform={`rotate(-90 ${BOUNDARY_X} ${H / 2 + 40})`}
          opacity="0.9"
        >
          DATA-BLIND BOUNDARY
        </text>

        {/* ===== sources → boundary arrows ===== */}
        {sourceToBoundary.map((d, i) => {
          const y = sourceY(i) + SRC_H / 2;
          return (
            <g key={`src-${i}`}>
              <path d={d} fill="none" stroke="url(#skyGrad)" strokeWidth="2.4" strokeLinecap="round" opacity="0.9" />
              <ArrowHead x={BOUNDARY_X - 16} y={y} dir="right" color="#38BDF8" size={7} />
              <Particle path={d} dur={2.4 + (i % 3) * 0.3} delay={i * 0.3} color="#38BDF8" r={3.2} />
            </g>
          );
        })}

        {/* ===== SOURCE CARDS ===== */}
        {SOURCES.map((src, i) => {
          const y = sourceY(i);
          const Ic = src.icon;
          return (
            <g key={src.label}>
              <rect
                x={SRC_X}
                y={y}
                width={SRC_W}
                height={SRC_H}
                rx="4"
                fill="url(#sourceFill)"
                stroke="#38BDF8"
                strokeWidth="1.4"
                opacity="0.98"
              />
              <rect x={SRC_X} y={y} width="3" height={SRC_H} fill="#38BDF8" rx="2" />
              <foreignObject x={SRC_X + 14} y={y + 14} width="26" height="26">
                <Ic size={22} color="#38BDF8" />
              </foreignObject>
              <text
                x={SRC_X + 48}
                y={y + 32}
                fill="#F8FAFC"
                fontFamily="Space Grotesk, sans-serif"
                fontSize="13"
                fontWeight="700"
                letterSpacing="0.3"
              >
                {src.label}
              </text>
            </g>
          );
        })}

        {/* ===== Metadata TOP line: boundary → SLM/LLM ===== */}
        <g>
          <path d={metaToSlm} fill="none" stroke="url(#flowGrad)" strokeWidth="2.6" strokeLinecap="round" />
          <ArrowHead x={SLM_X - 4} y={SLM_Y + 42} dir="right" color="#10B981" size={9} />
          <Particle path={metaToSlm} dur={2.4} color="#10B981" r={4} />
          <rect x={(BOUNDARY_X + SLM_X) / 2 - 44} y={210} width="88" height="20" rx="10" fill="#0A1F1A" stroke="#10B981" strokeWidth="1" opacity="0.95" />
          <text
            x={(BOUNDARY_X + SLM_X) / 2}
            y={224}
            fill="#10B981"
            fontFamily="JetBrains Mono, monospace"
            fontSize="10"
            fontWeight="800"
            letterSpacing="2.4"
            textAnchor="middle"
          >
            METADATA
          </text>
        </g>

        {/* ===== Metadata BOTTOM line: boundary → KG ===== */}
        <g>
          <path d={metaToKg} fill="none" stroke="url(#flowGrad)" strokeWidth="2.6" strokeLinecap="round" />
          <ArrowHead x={KG_CX - KG_RX - 4} y={KG_CY} dir="right" color="#10B981" size={9} />
          <Particle path={metaToKg} dur={2.8} delay={0.4} color="#10B981" r={4} />
          <rect x={(BOUNDARY_X + KG_CX - KG_RX) / 2 - 44} y={455} width="88" height="20" rx="10" fill="#0A1F1A" stroke="#10B981" strokeWidth="1" opacity="0.95" />
          <text
            x={(BOUNDARY_X + KG_CX - KG_RX) / 2}
            y={469}
            fill="#10B981"
            fontFamily="JetBrains Mono, monospace"
            fontSize="10"
            fontWeight="800"
            letterSpacing="2.4"
            textAnchor="middle"
          >
            METADATA
          </text>
        </g>

        {/* ===== KNOWLEDGE GRAPH ===== */}
        <ellipse cx={KG_CX} cy={KG_CY} rx={KG_RX + 28} ry={KG_RY + 20} fill="url(#kgGlow)" />
        <ellipse
          cx={KG_CX}
          cy={KG_CY}
          rx={KG_RX}
          ry={KG_RY}
          fill="url(#kgFill)"
          stroke="#10B981"
          strokeWidth="2.2"
          filter="url(#softGlow)"
        />
        <text
          x={KG_CX}
          y={KG_CY - 4}
          fill="#F8FAFC"
          fontFamily="Space Grotesk, sans-serif"
          fontSize="16"
          fontWeight="900"
          letterSpacing="1.4"
          textAnchor="middle"
        >
          KNOWLEDGE
        </text>
        <text
          x={KG_CX}
          y={KG_CY + 16}
          fill="#F8FAFC"
          fontFamily="Space Grotesk, sans-serif"
          fontSize="16"
          fontWeight="900"
          letterSpacing="1.4"
          textAnchor="middle"
        >
          GRAPH
        </text>

        {/* ===== USER INPUT ===== */}
        <rect
          x={USER_X}
          y={USER_Y}
          width={USER_W}
          height={USER_H}
          rx="4"
          fill="url(#userFill)"
          stroke="#38BDF8"
          strokeWidth="1.5"
        />
        <rect x={USER_X} y={USER_Y} width="3" height={USER_H} fill="#38BDF8" rx="2" />
        <foreignObject x={USER_X + 14} y={USER_Y + 16} width="26" height="26">
          <User size={22} color="#38BDF8" />
        </foreignObject>
        <text
          x={USER_X + 48}
          y={USER_Y + 26}
          fill="#F8FAFC"
          fontFamily="Space Grotesk, sans-serif"
          fontSize="13"
          fontWeight="800"
          letterSpacing="0.6"
        >
          USER INPUT
        </text>
        <text
          x={USER_X + 48}
          y={USER_Y + 44}
          fill="#94A3B8"
          fontFamily="JetBrains Mono, monospace"
          fontSize="10"
          fontWeight="600"
        >
          Plain-language question
        </text>

        {/* ===== USER → KG (upward) ===== */}
        <g>
          <path d={userToKg} fill="none" stroke="url(#skyGrad)" strokeWidth="2.4" strokeLinecap="round" />
          <ArrowHead x={KG_CX} y={KG_CY + KG_RY + 4} dir="up" color="#38BDF8" size={8} />
          <Particle path={userToKg} dur={2.2} color="#38BDF8" r={3.4} />
        </g>

        {/* ===== KG → SLM/LLM (diagonal up-right) ===== */}
        <g>
          <path d={kgToSlm} fill="none" stroke="url(#emeraldGrad)" strokeWidth="2.6" strokeLinecap="round" />
          <ArrowHead x={SLM_X + SLM_W * 0.6} y={SLM_Y + SLM_H - 4} dir="up" color="#10B981" size={8} />
          <Particle path={kgToSlm} dur={2.6} color="#10B981" r={3.6} />
        </g>

        {/* ===== SLM / LLM combined block ===== */}
        <rect
          x={SLM_X - 6}
          y={SLM_Y - 6}
          width={SLM_W + 12}
          height={SLM_H + 12}
          rx="8"
          fill="#10B981"
          opacity="0.08"
        />
        <rect
          x={SLM_X}
          y={SLM_Y}
          width={SLM_W}
          height={SLM_H}
          rx="6"
          fill="url(#slmFill)"
          stroke="#38BDF8"
          strokeWidth="2"
        />
        <rect x={SLM_X} y={SLM_Y} width={SLM_W} height="3" fill="url(#flowGrad)" />
        <text
          x={SLM_X + SLM_W / 2}
          y={SLM_Y + 56}
          fill="#F8FAFC"
          fontFamily="Space Grotesk, sans-serif"
          fontSize="22"
          fontWeight="900"
          letterSpacing="0.8"
          textAnchor="middle"
        >
          SLM on premise
        </text>
        <g>
          <line x1={SLM_X + 40} y1={SLM_Y + 78} x2={SLM_X + SLM_W / 2 - 20} y2={SLM_Y + 78} stroke="#334155" strokeWidth="1" />
          <text
            x={SLM_X + SLM_W / 2}
            y={SLM_Y + 83}
            fill="#64748B"
            fontFamily="JetBrains Mono, monospace"
            fontSize="12"
            fontWeight="800"
            letterSpacing="3"
            textAnchor="middle"
          >
            OR
          </text>
          <line x1={SLM_X + SLM_W / 2 + 20} y1={SLM_Y + 78} x2={SLM_X + SLM_W - 40} y2={SLM_Y + 78} stroke="#334155" strokeWidth="1" />
        </g>
        <text
          x={SLM_X + SLM_W / 2}
          y={SLM_Y + 118}
          fill="#F8FAFC"
          fontFamily="Space Grotesk, sans-serif"
          fontSize="22"
          fontWeight="900"
          letterSpacing="0.8"
          textAnchor="middle"
        >
          LLMs
        </text>
        <text
          x={SLM_X + SLM_W / 2}
          y={SLM_Y + 148}
          fill="#10B981"
          fontFamily="JetBrains Mono, monospace"
          fontSize="10"
          fontWeight="700"
          letterSpacing="1.6"
          textAnchor="middle"
        >
          METADATA INPUT ONLY
        </text>

        {/* ===== SLM/LLM → Agent ===== */}
        <g>
          <path d={slmToAgent} fill="none" stroke="url(#flowGrad)" strokeWidth="2.8" strokeLinecap="round" />
          <ArrowHead x={AGENT_X - 4} y={AGENT_Y + AGENT_H / 2} dir="right" color="#10B981" size={9} />
          <Particle path={slmToAgent} dur={1.8} color="#10B981" r={4} />
        </g>

        {/* ===== SPECIFIC AGENT ===== */}
        <rect
          x={AGENT_X}
          y={AGENT_Y}
          width={AGENT_W}
          height={AGENT_H}
          rx="6"
          fill="url(#agentFill)"
          stroke="#38BDF8"
          strokeWidth="2"
        />
        <rect x={AGENT_X} y={AGENT_Y} width={AGENT_W} height="3" fill="url(#flowGrad)" />
        <text
          x={AGENT_X + AGENT_W / 2}
          y={AGENT_Y + 38}
          fill="#F8FAFC"
          fontFamily="Space Grotesk, sans-serif"
          fontSize="16"
          fontWeight="900"
          letterSpacing="1.2"
          textAnchor="middle"
        >
          SPECIFIC AGENT
        </text>
        <text
          x={AGENT_X + AGENT_W / 2}
          y={AGENT_Y + 62}
          fill="#38BDF8"
          fontFamily="JetBrains Mono, monospace"
          fontSize="9.5"
          fontWeight="700"
          letterSpacing="1.6"
          textAnchor="middle"
        >
          PURPOSE-BUILT
        </text>
        <text
          x={AGENT_X + AGENT_W / 2}
          y={AGENT_Y + 76}
          fill="#38BDF8"
          fontFamily="JetBrains Mono, monospace"
          fontSize="9.5"
          fontWeight="700"
          letterSpacing="1.6"
          textAnchor="middle"
        >
          PER WORKFLOW
        </text>

        {/* ===== Agent → Trusted Output ===== */}
        <g>
          <path d={agentToOut} fill="none" stroke="url(#emeraldGrad)" strokeWidth="3" strokeLinecap="round" />
          <ArrowHead x={OUT_X - 4} y={OUT_Y + OUT_H / 2} dir="right" color="#10B981" size={10} />
          <Particle path={agentToOut} dur={1.8} color="#10B981" r={4.4} />
        </g>

        {/* ===== TRUSTED OUTPUT (hero) ===== */}
        <ellipse
          cx={OUT_X + OUT_W / 2}
          cy={OUT_Y + OUT_H / 2}
          rx={OUT_W / 2 + 30}
          ry={OUT_H / 2 + 24}
          fill="url(#outGlow)"
        />
        <rect
          x={OUT_X - 4}
          y={OUT_Y - 4}
          width={OUT_W + 8}
          height={OUT_H + 8}
          rx="10"
          fill="#10B981"
          opacity="0.1"
        />
        <rect
          x={OUT_X}
          y={OUT_Y}
          width={OUT_W}
          height={OUT_H}
          rx="8"
          fill="url(#outFill)"
          stroke="#10B981"
          strokeWidth="2.4"
          filter="url(#softGlow)"
        />
        <rect x={OUT_X} y={OUT_Y} width={OUT_W} height="4" fill="#10B981" />
        <text
          x={OUT_X + OUT_W / 2}
          y={OUT_Y + 42}
          fill="#F8FAFC"
          fontFamily="Space Grotesk, sans-serif"
          fontSize="18"
          fontWeight="900"
          letterSpacing="1.5"
          textAnchor="middle"
        >
          RESPONSE
        </text>
        <text
          x={OUT_X + OUT_W / 2}
          y={OUT_Y + 64}
          fill="#10B981"
          fontFamily="JetBrains Mono, monospace"
          fontSize="11"
          fontWeight="800"
          letterSpacing="2.4"
          textAnchor="middle"
        >
          TRUSTED OUTPUT
        </text>
        <line
          x1={OUT_X + 30}
          y1={OUT_Y + 80}
          x2={OUT_X + OUT_W - 30}
          y2={OUT_Y + 80}
          stroke="#10B981"
          strokeWidth="1"
          opacity="0.4"
        />
        <text
          x={OUT_X + OUT_W / 2}
          y={OUT_Y + 104}
          fill="#CBD5E1"
          fontFamily="JetBrains Mono, monospace"
          fontSize="10.5"
          fontWeight="700"
          letterSpacing="1.3"
          textAnchor="middle"
        >
          Dashboards · Root-cause
        </text>
        <text
          x={OUT_X + OUT_W / 2}
          y={OUT_Y + 124}
          fill="#CBD5E1"
          fontFamily="JetBrains Mono, monospace"
          fontSize="10.5"
          fontWeight="700"
          letterSpacing="1.3"
          textAnchor="middle"
        >
          Alarms · Recommendations
        </text>
        <text
          x={OUT_X + OUT_W / 2}
          y={OUT_Y + 150}
          fill="#10B981"
          fontFamily="JetBrains Mono, monospace"
          fontSize="9"
          fontWeight="700"
          letterSpacing="1.8"
          textAnchor="middle"
          opacity="0.75"
        >
          ◆ TRUSTED OUTPUT ◆
        </text>
      </svg>
    </div>
  );
}
